import { Slot, Slottable } from "@radix-ui/react-slot";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { interactionSoundProps } from "../interaction-sound";
import { cn } from "../lib/cn";

/**
 * LinkButton. Outlined teal-700 text-link with trailing arrow chevron,
 * 1px mist-dark border, transparent fill, teal-50 hover. The
 * "low-emphasis CTA" used inside cards: SettingsPage CardLink,
 * AttentionPage empty-state secondary actions, ScreenGoal "not-sure"
 * button, anywhere a card needs a route-aware affordance without
 * competing with a primary Button.
 *
 * Use `asChild` to render as a router Link / <a>:
 *
 *   <LinkButton asChild>
 *     <Link to="/today">Open today</Link>
 *   </LinkButton>
 */
export interface LinkButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** When true, renders children as the root element (typically a router Link). */
  asChild?: boolean;
  /** Trailing icon. Defaults to a right-pointing chevron. */
  trailingIcon?: ReactNode | false;
}

const ArrowRight = (
  <svg
    width={12}
    height={12}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    className="shrink-0"
  >
    <path
      d="M2.5 8h11M9 3.5L13.5 8 9 12.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>(function LinkButton(
  { asChild, trailingIcon = ArrowRight, className, children, ...rest },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : "button"}
      {...interactionSoundProps(rest.disabled ? "none" : "action")}
      className={cn(
        "inline-flex w-max items-center gap-1.5 whitespace-nowrap rounded-lg border border-mist-dark/40 bg-transparent px-3.5 py-2 font-sans text-[12.5px] font-medium tracking-[-0.005em] text-teal-700 transition-colors duration-200 ease-[var(--ease-out-expo)] hover:border-teal-300 hover:bg-teal-50 hover:text-teal-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600",
        className,
      )}
      {...rest}
    >
      <Slottable>{children}</Slottable>
      {trailingIcon}
    </Comp>
  );
});
